import type { ReactNode } from "react";
import type { EmptyConfig, QueryLike } from "@/components/request-boundary";
import { resolveRequestErrorDisplay } from "@/components/request-error-display";
import { useI18n } from "@/locales/i18n-provider";
import { isSessionExpiredError } from "@/request/request-client/session-expired";
import { DataTableStateProvider } from "./data-table-state-provider";

type DataTableRequestBoundaryProps<TData> = {
  query: QueryLike<TData>;
  loadingLabel?: string;
  empty?: EmptyConfig;
  children: ReactNode | ((data: TData | undefined) => ReactNode);
};

/** 表格请求边界始终渲染 children，所有反馈由表体消费，不能替换表头。 */
export function DataTableRequestBoundary<TData>({ query, loadingLabel, empty, children }: DataTableRequestBoundaryProps<TData>) {
  const { t } = useI18n();
  const isError = query.isError && !(isSessionExpiredError(query.error) && query.data !== undefined);
  const display = isError ? resolveRequestErrorDisplay(query.error, t) : undefined;

  return (
    <DataTableStateProvider
      value={{
        isLoading: query.isPending,
        isRefreshing: query.isFetching && !query.isPending,
        isError,
        loadingLabel,
        errorTitle: display?.title,
        errorDescription: display?.description,
        onRetry: display?.retryable
          ? () => {
              void query.refetch();
            }
          : undefined,
        emptyTitle: empty?.title,
        emptyDescription: empty?.description,
        emptyMedia: empty?.icon ?? (empty?.illustration ? <img alt="" className="h-32 w-auto" src={empty.illustration} /> : undefined),
        emptyActions: empty?.actions,
      }}
    >
      {typeof children === "function" ? children(query.data) : children}
    </DataTableStateProvider>
  );
}
